import { ApiError, demoApi } from "@/api/client";
import { withDrainLock, DRAIN_LOCKS } from "./drainLock";
import type { CreateEntryRequest } from "@/api/types";
import { createOutboxStore, type OutboxStore, type QueuedEntry } from "./outboxStore";
import { mappingQueue } from "./mappingQueue";
import { registerDrainTriggers } from "./drainTriggers";

type Listener = (items: QueuedEntry[]) => void;

function newClientId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  // Fallback for older iOS Safari (< 15.4) without crypto.randomUUID. This id
  // never leaves the device, so the collision odds are an acceptable trade
  // against a hard runtime error on an in-scope booth browser.
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * On-device outbox manager for entries that failed to submit because the
 * phone is offline (of-002), and their replay once it isn't (of-003).
 *
 * Deliberately a plain singleton, not React state: the outbox has to persist
 * (and keep draining) whether or not `QuickEntry` happens to be mounted —
 * `useOutbox` is a read-only subscription onto it, not the source of truth.
 */
class OutboxManager {
  private storePromise: Promise<OutboxStore> | null = null;
  private listeners = new Set<Listener>();
  private draining = false;

  private getStore(): Promise<OutboxStore> {
    if (!this.storePromise) this.storePromise = createOutboxStore();
    return this.storePromise;
  }

  async list(): Promise<QueuedEntry[]> {
    const store = await this.getStore();
    return store.all();
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  private async notify(): Promise<void> {
    const items = await this.list();
    for (const fn of this.listeners) fn(items);
  }

  /**
   * Queue an entry that could not reach the server because the device is
   * offline. `mapping` carries the "it learned" teaching write alongside it
   * (build task #3) — set only when the entry came from an accepted or
   * corrected receipt scan, mirroring the same condition `QuickEntry.tsx`
   * uses online.
   */
  async enqueue(
    request: CreateEntryRequest,
    mapping?: { vendorId: string; accountId: string } | null,
  ): Promise<void> {
    const store = await this.getStore();
    await store.add({ clientId: newClientId(), request, mapping: mapping ?? null, queuedAt: Date.now() });
    await this.notify();
  }

  /**
   * Replay the outbox in order (of-003).
   *
   * Sequential and awaited one at a time — not `Promise.all` — because the
   * task requires in-order replay, and only awaiting each POST before
   * starting the next makes "in order" actually true rather than merely
   * likely on a fast connection.
   *
   * A guard flag prevents two overlapping drains (e.g. the 'online' event
   * firing again while a previous drain is still in flight on a flaky
   * reconnect) from posting the same queued item twice.
   */
  async drain(): Promise<void> {
    if (this.draining) return;
    this.draining = true;
    try {
      // ACROSS TABS, NOT JUST ACROSS CALLS. `this.draining` is one instance's flag, so it
      // does exactly what its comment above says WITHIN a tab and nothing at all between
      // two. Both tabs share the same IndexedDB queue.
      //
      // MEASURED 2026-09-08 on the booth tenant: two tabs open on /entry, offline, one
      // entry filed in the first, then back online —
      //
      //     entries: 2 (expected 1)
      //     42.42 @ 09:18:16.899 | 42.42 @ 09:18:16.899
      //
      // The same visitor's money on the wall twice, to the millisecond. The spec waves
      // multi-tab off — "one visitor, one tab" — and for a VISITOR that holds: a stranger's
      // browser opens once. The operator's tethered Android is the device that stays open
      // all day, and it is the one the lead beat runs on.
      await withDrainLock(DRAIN_LOCKS.outbox, async () => {
        try {
          await this.drainOnce();
        } finally {
          // A tab queued behind the lock may find the store already emptied by the other tab and
          // process nothing (or bail still-offline); without this its subscribers keep a stale view.
          await this.notify();
        }
      });
    } finally {
      this.draining = false;
    }
  }

  /** One pass over the queue. Only ever called while the origin-wide drain lock is held. */
  private async drainOnce(): Promise<void> {
    {
      const store = await this.getStore();
      const items = await store.all();
      for (const item of items) {
        try {
          await demoApi.createEntry(item.request);
        } catch (err) {
          if (err instanceof ApiError && err.status === 0) {
            // Still offline (or offline again mid-drain). Stop here, in
            // order — the next 'online' event (or a future manual retry)
            // resumes from this same item rather than skipping ahead.
            return;
          }
          // A real server rejection (validation, 5xx) is not a "we were
          // offline" case. Leave it queued and visible rather than silently
          // dropping it, but move on so one bad row can't wedge every later
          // entry behind it forever.
          console.warn("An outbox entry was rejected by the server; leaving it queued.", err);
          continue;
        }

        // THE MAPPING LESSON (build task #3), best-effort like the online
        // path — but never silently lost if the network drops again right
        // here: queued in `mappingQueue` rather than just logged.
        if (item.mapping) {
          try {
            await demoApi.confirmMapping(item.mapping);
          } catch (err) {
            if (err instanceof ApiError && err.status === 0) {
              await mappingQueue.enqueue(item.mapping);
            } else {
              console.warn("Entry saved, but the vendor mapping was not recorded.", err);
            }
          }
        }

        await store.remove(item.clientId);
        await this.notify();
      }
    }
  }
}


export const outbox = new OutboxManager();

registerDrainTriggers(outbox);
